import { DatePicker, Form, FormInstance, Input, InputNumber, Select } from 'antd';
import moment from 'moment';
import React from 'react';
import AutocompleteDropdown from '../../../components/autocomplete-dropdown';
import DrawerButtons from '../../../components/drawer-buttons/index';
import { DropdownOption } from '../../../core/models/DropdownOption';
import { ActionOption, DrawerState, ModuleName } from '../../../core/models/enum';
import {
    confirmDropdownHelper,
    filterDropdownOptions,
    getCitiesHelper,
    getCountriesHelper,
} from '../../../helpers/AutocompleteDropdownHelper';
import { isUserAllowed } from '../../../helpers/CheckPermissionHelper';
import { filterOptionForSearch } from '../../../helpers/normalizeForSearch';
import {
    getCompetitionsToDropdownOptions,
    getMatchTeamsToDropdownOptions,
} from '../../../helpers/FetchAndTransformHelpers';
import { formItemLayout724 } from '../../../helpers/FormLayoutHelper';
import { mergeMatchStageSelectOptions } from '../../../helpers/matchCompetitionStages';
import {
    PERIOD_COUNT_OPTIONS,
    timingFormValuesFromFields,
    timingPayloadFromForm,
} from '../../../helpers/matchTiming';
import { success } from '../../../helpers/NotificationHelper';
import { createHelper, handleDeleteHelper, updateHelper } from '../../../helpers/SubmitHelper';
import { customArrayValidationHelper } from '../../../helpers/ValidationHelper';
import { CompetitionsClient, MatchesClient, MatchStatusEnum } from '../../../utils/api';
import MatchAdminEventActions from '../match-admin-event-actions';
import { Props } from './index';

interface State {
    isSaving: boolean;
    countries: DropdownOption[];
    cities: DropdownOption[];
    teams: DropdownOption[];
    competitions: DropdownOption[];
    selectedCountry?: DropdownOption[];
    selectedCity?: DropdownOption[];
    selectedHomeTeam?: DropdownOption[];
    selectedAwayTeam?: DropdownOption[];
    selectedCompetition?: DropdownOption[];
    extraCompetitionStagesForDropdown: string[];
    timingHydrated: boolean;
}

class MatchForm extends React.PureComponent<Props, State> {
    private formRef = React.createRef<FormInstance>();

    public constructor(props: Props) {
        super(props);

        this.state = {
            isSaving: false,
            countries: [],
            cities: [],
            teams: [],
            competitions: [],
            extraCompetitionStagesForDropdown: [],
            timingHydrated: !!props.matchFromProps,
        };
    }

    public componentDidMount = (): void => {
        const { matchFromProps } = this.props;

        this.getCountries();
        if (!(matchFromProps && matchFromProps.countryId)) {
            this.getCities();
        }
        this.getTeams();
        this.getCompetitions();
        this.bootstrapCompetitionStages();
    };

    private bootstrapCompetitionStages = (): void => {
        const { matchFromProps, initialValues } = this.props;
        if (matchFromProps?.competition) {
            this.setState({
                extraCompetitionStagesForDropdown:
                    matchFromProps.competition.extraCompetitionStages || [],
            });
            return;
        }
        const cid = initialValues?.competitionId ?? matchFromProps?.competitionId;
        if (cid) {
            this.loadExtraStagesForCompetition(cid);
        }
    };

    private loadExtraStagesForCompetition = async (competitionId: number): Promise<void> => {
        try {
            const detail = await new CompetitionsClient().getById(competitionId);
            this.setState({
                extraCompetitionStagesForDropdown: detail.extraCompetitionStages || [],
                timingHydrated: true,
            });
            const { matchFromProps } = this.props;
            if (!matchFromProps) {
                const timing = timingFormValuesFromFields(detail);
                this.formRef.current?.setFieldsValue(timing);
            }
        } catch {
            this.setState({ extraCompetitionStagesForDropdown: [], timingHydrated: true });
        }
    };

    private onCompetitionDropdownConfirm = (options: DropdownOption[]): void => {
        const prevId = this.state.selectedCompetition?.[0]?.id;
        const next = confirmDropdownHelper(options);
        this.setState({ selectedCompetition: next, timingHydrated: false });
        const nextId = next?.[0]?.id;
        if (nextId && nextId !== prevId) {
            this.formRef.current?.setFieldsValue({ competitionStage: undefined });
            this.loadExtraStagesForCompetition(parseInt(nextId, 10));
        } else if (!nextId) {
            this.setState({ extraCompetitionStagesForDropdown: [] });
            this.formRef.current?.setFieldsValue({ competitionStage: undefined });
        }
    };

    private getCountries = async (): Promise<void> => {
        const { matchFromProps } = this.props;
        const countryId = matchFromProps && matchFromProps.countryId;

        const countryHelper = await getCountriesHelper(countryId);

        if (countryId) {
            this.getCities(countryId);
        }

        this.setState({
            countries: countryHelper.entities,
            selectedCountry: countryHelper.selectedEntity,
        });
    };

    private getCities = async (countryId?: number): Promise<void> => {
        const { matchFromProps } = this.props;
        const cityId = matchFromProps && matchFromProps.cityId;

        const cityHelper = await getCitiesHelper(countryId, cityId);

        this.setState({
            cities: cityHelper.entities,
            selectedCity: cityHelper.selectedEntity,
        });
    };

    private getTeams = async (): Promise<void> => {
        const { matchFromProps, initialValues } = this.props;

        const results = await getMatchTeamsToDropdownOptions(
            matchFromProps && matchFromProps.homeTeamId,
            matchFromProps && matchFromProps.awayTeamId,
            initialValues && initialValues.teams
        );

        this.setState({
            teams: results.entities,
            selectedHomeTeam: results.selectedEntity,
            selectedAwayTeam: results.selectedSecondEntity,
        });
    };

    private getCompetitions = async (): Promise<void> => {
        const { matchFromProps, initialValues } = this.props;
        const competitionId = matchFromProps && matchFromProps.competitionId;
        const preSelectedCompetitionId = initialValues && initialValues.competitionId;

        const results = await getCompetitionsToDropdownOptions(
            competitionId || preSelectedCompetitionId
        );

        this.setState({
            competitions: results.entities,
            selectedCompetition: results.selectedEntity,
        });
    };

    private getCompetitionStageSelectOptions = (): { value: string; label: string }[] => {
        const { matchFromProps } = this.props;
        const current = matchFromProps?.competitionStage;
        return mergeMatchStageSelectOptions(this.state.extraCompetitionStagesForDropdown, current);
    };

    private onSubmit = async (values: any): Promise<void> => {
        const {
            selectedCountry,
            selectedCity,
            selectedHomeTeam,
            selectedAwayTeam,
            selectedCompetition,
            timingHydrated,
        } = this.state;
        const { matchFromProps } = this.props;
        const matchClient = new MatchesClient();

        this.setState({
            isSaving: true,
        });

        const competitionId =
            selectedCompetition && selectedCompetition.length > 0
                ? parseInt(selectedCompetition[0].id, 10)
                : undefined;
        let formValues = values;
        if (!matchFromProps && competitionId && !timingHydrated) {
            await this.loadExtraStagesForCompetition(competitionId);
            formValues = { ...values, ...this.formRef.current?.getFieldsValue() };
        }

        const timing = matchFromProps
            ? {
                  matchSegmentId: matchFromProps.matchSegmentId,
                  matchSegmentLength: matchFromProps.matchSegmentLength,
                  shotClockMaxSeconds: matchFromProps.shotClockMaxSeconds,
                  shotClockResetSeconds: matchFromProps.shotClockResetSeconds,
                  exclusionDurationSeconds: matchFromProps.exclusionDurationSeconds,
                  brutalityDurationSeconds: matchFromProps.brutalityDurationSeconds,
              }
            : timingPayloadFromForm(formValues);

        const request = {
            ...formValues,
            ...timing,
            id: matchFromProps ? matchFromProps.id : undefined,
            homeTeamGoalsQ1: matchFromProps?.homeTeamGoalsQ1,
            homeTeamGoalsQ2: matchFromProps?.homeTeamGoalsQ2,
            homeTeamGoalsQ3: matchFromProps?.homeTeamGoalsQ3,
            homeTeamGoalsQ4: matchFromProps?.homeTeamGoalsQ4,
            homeTeamGoalsOT: matchFromProps?.homeTeamGoalsOT,
            awayTeamGoalsQ1: matchFromProps?.awayTeamGoalsQ1,
            awayTeamGoalsQ2: matchFromProps?.awayTeamGoalsQ2,
            awayTeamGoalsQ3: matchFromProps?.awayTeamGoalsQ3,
            awayTeamGoalsQ4: matchFromProps?.awayTeamGoalsQ4,
            awayTeamGoalsOT: matchFromProps?.awayTeamGoalsOT,
            statusId: matchFromProps ? matchFromProps.statusId : MatchStatusEnum.Not_Started,
            countryId:
                selectedCountry && selectedCountry.length > 0
                    ? parseInt(selectedCountry[0].id, 10)
                    : undefined,
            cityId:
                selectedCity && selectedCity.length > 0
                    ? parseInt(selectedCity[0].id, 10)
                    : undefined,
            homeTeamId:
                selectedHomeTeam && selectedHomeTeam.length > 0
                    ? parseInt(selectedHomeTeam[0].id, 10)
                    : undefined,
            awayTeamId:
                selectedAwayTeam && selectedAwayTeam.length > 0
                    ? parseInt(selectedAwayTeam[0].id, 10)
                    : undefined,
            competitionId:
                selectedCompetition && selectedCompetition.length > 0
                    ? parseInt(selectedCompetition[0].id, 10)
                    : undefined,
        };

        if (request.id) {
            updateHelper(matchClient, request, this.handleSuccessfullySaving);
        } else {
            createHelper(matchClient, request, this.handleSuccessfullySaving);
        }

        this.setState({
            isSaving: false,
        });
    };

    private handleSuccessfullySaving = (): void => {
        const { matchFromProps, handleClose, refreshAfterSave } = this.props;

        success('Match successfully saved.');
        refreshAfterSave();
        handleClose(
            matchFromProps ? DrawerState.Read : DrawerState.Closed,
            matchFromProps ? matchFromProps.id.toString() : undefined
        );
    };

    private handleDeleteMatch = async (): Promise<void> => {
        const { matchFromProps, onDeleted, handleClose } = this.props;
        if (!matchFromProps?.id) {
            return;
        }
        await handleDeleteHelper(
            new MatchesClient(),
            'Match successfully deleted.',
            (): void => {
                void Promise.resolve(onDeleted?.()).finally((): void => {
                    handleClose(DrawerState.Closed, undefined);
                });
            },
            matchFromProps.id
        );
    };

    private confirmCountry = (option: DropdownOption[]): void => {
        const selectedCountry = confirmDropdownHelper(option);
        this.setState({
            selectedCountry,
            selectedCity: undefined,
        });
        if (selectedCountry) {
            this.getCities(parseInt(selectedCountry[0].id, 10));
        }
    };

    public render(): React.ReactElement {
        const {
            isSaving,
            selectedCountry,
            selectedCity,
            selectedHomeTeam,
            selectedAwayTeam,
            selectedCompetition,
            competitions,
            teams,
            countries,
            cities,
        } = this.state;
        const {
            handleClose,
            matchFromProps,
            initialValues,
            showDeleteInDrawer,
            userProfile,
            onDeleted,
            refreshAfterSave,
        } = this.props;

        const canDeleteInDrawer =
            !!showDeleteInDrawer &&
            !!matchFromProps?.id &&
            !!userProfile &&
            !!onDeleted &&
            isUserAllowed(userProfile, ModuleName.Matches, ActionOption.Delete, matchFromProps.id);

        const competitionStageOptions = this.getCompetitionStageSelectOptions();
        const competitionIdForStages =
            (initialValues && initialValues.competitionId) ||
            (selectedCompetition && selectedCompetition.length > 0
                ? parseInt(selectedCompetition[0].id, 10)
                : undefined);
        const timingDefaults = timingFormValuesFromFields(matchFromProps);

        return (
            <Form ref={this.formRef} onFinish={this.onSubmit} {...formItemLayout724}>
                <DrawerButtons
                    isSaving={isSaving}
                    onCancelAction={(): void =>
                        handleClose(
                            matchFromProps ? DrawerState.Read : DrawerState.Closed,
                            matchFromProps ? matchFromProps.id.toString() : undefined
                        )
                    }
                    onDeleteAction={canDeleteInDrawer ? this.handleDeleteMatch : undefined}
                    deleteConfirmMessage="Are you sure you want to delete this match?"
                />
                {matchFromProps?.id && (
                    <MatchAdminEventActions
                        match={matchFromProps}
                        userProfile={userProfile}
                        onCompleted={refreshAfterSave}
                    />
                )}
                <div className="drawer-content">
                    <Form.Item
                        name="homeTeamId"
                        label="Home team"
                        rules={[
                            {
                                required: true,
                                validator: (_rule: any, value: any, callback: any): void =>
                                    customArrayValidationHelper(
                                        _rule,
                                        value,
                                        callback,
                                        'Home team field is required!',
                                        selectedHomeTeam
                                    ),
                            },
                        ]}
                    >
                        <AutocompleteDropdown
                            placeholder="Choose home team"
                            getOptionsFrontend={(value: string): DropdownOption[] =>
                                filterDropdownOptions(
                                    value,
                                    selectedAwayTeam && selectedAwayTeam.length > 0
                                        ? teams.filter(
                                              (t): boolean => t.id !== selectedAwayTeam[0].id
                                          )
                                        : teams
                                )
                            }
                            initialValues={selectedHomeTeam}
                            confirmDirty={(options: DropdownOption[]): void =>
                                this.setState({
                                    selectedHomeTeam: confirmDropdownHelper(options),
                                })
                            }
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="awayTeamId"
                        label="Away team"
                        rules={[
                            {
                                required: true,
                                validator: (_rule: any, value: any, callback: any): void =>
                                    customArrayValidationHelper(
                                        _rule,
                                        value,
                                        callback,
                                        'Away team field is required!',
                                        selectedAwayTeam
                                    ),
                            },
                        ]}
                    >
                        <AutocompleteDropdown
                            placeholder="Choose away team"
                            getOptionsFrontend={(value: string): DropdownOption[] =>
                                filterDropdownOptions(
                                    value,
                                    selectedHomeTeam && selectedHomeTeam.length > 0
                                        ? teams.filter(
                                              (t): boolean => t.id !== selectedHomeTeam[0].id
                                          )
                                        : teams
                                )
                            }
                            initialValues={selectedAwayTeam}
                            confirmDirty={(options: DropdownOption[]): void =>
                                this.setState({
                                    selectedAwayTeam: confirmDropdownHelper(options),
                                })
                            }
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="competitionId"
                        label="Competition"
                        rules={[
                            {
                                required: true,
                                validator: (_rule: any, value: any, callback: any): void =>
                                    customArrayValidationHelper(
                                        _rule,
                                        value,
                                        callback,
                                        'Competition field is required!',
                                        selectedCompetition
                                    ),
                            },
                        ]}
                    >
                        <AutocompleteDropdown
                            placeholder="Choose competition"
                            getOptionsFrontend={(value: string): DropdownOption[] =>
                                filterDropdownOptions(value, competitions)
                            }
                            initialValues={selectedCompetition}
                            confirmDirty={this.onCompetitionDropdownConfirm}
                            style={{ width: '100%' }}
                            disabled={!!(initialValues && initialValues.competitionId)}
                            isAllowedToClear
                        />
                    </Form.Item>
                    <Form.Item
                        name="competitionStage"
                        label="Competition stage"
                        initialValue={matchFromProps ? matchFromProps.competitionStage : undefined}
                        rules={[
                            {
                                required: true,
                                message: 'Competition stage is required.',
                            },
                        ]}
                    >
                        <Select
                            placeholder={
                                competitionIdForStages
                                    ? 'Choose competition stage'
                                    : 'Choose a competition first'
                            }
                            allowClear={false}
                            showSearch
                            filterOption={filterOptionForSearch}
                            optionFilterProp="label"
                            disabled={!competitionIdForStages}
                            options={competitionStageOptions}
                        />
                    </Form.Item>
                    <Form.Item
                        name="startDate"
                        label="Start"
                        initialValue={
                            matchFromProps?.startDate ? moment(matchFromProps.startDate) : null
                        }
                        rules={[
                            {
                                required: true,
                                message: 'Start is required!',
                            },
                        ]}
                    >
                        <DatePicker format="DD.MM.YYYY. HH:mm" style={{ width: '100%' }} showTime />
                    </Form.Item>
                    <Form.Item
                        name="venue"
                        label="Venue"
                        initialValue={matchFromProps ? matchFromProps.venue : undefined}
                        rules={[{ max: 200, message: 'Too long input!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="round"
                        label="Round"
                        initialValue={matchFromProps ? matchFromProps.round : undefined}
                        rules={[{ max: 200, message: 'Too long input!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="number"
                        label="Game No."
                        initialValue={matchFromProps ? matchFromProps.number : undefined}
                        rules={[{ max: 200, message: 'Too long input!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="country" label="Country">
                        <AutocompleteDropdown
                            placeholder="Choose country"
                            getOptionsFrontend={(value: string): DropdownOption[] =>
                                filterDropdownOptions(value, countries)
                            }
                            initialValues={selectedCountry}
                            confirmDirty={this.confirmCountry}
                            style={{ width: '100%' }}
                            isAllowedToClear
                        />
                    </Form.Item>
                    <Form.Item name="city" label="City">
                        <AutocompleteDropdown
                            placeholder="Choose city"
                            getOptionsFrontend={(value: string): DropdownOption[] =>
                                filterDropdownOptions(value, cities)
                            }
                            initialValues={selectedCity}
                            confirmDirty={(options: DropdownOption[]): void =>
                                this.setState({
                                    selectedCity: confirmDropdownHelper(options),
                                })
                            }
                            style={{ width: '100%' }}
                            isAllowedToClear
                        />
                    </Form.Item>
                    <Form.Item
                        name="endDate"
                        label="End"
                        initialValue={
                            matchFromProps?.endDate ? moment(matchFromProps.endDate) : null
                        }
                    >
                        <DatePicker format="DD.MM.YYYY. HH:mm" style={{ width: '100%' }} showTime />
                    </Form.Item>
                    {/* Match segment: stored as MatchSegmentId; UI is Period count 1-4. */}
                    <Form.Item
                        name="periodCount"
                        label="Period count"
                        initialValue={timingDefaults.periodCount}
                        rules={[{ required: true, message: 'Period count is required!' }]}
                    >
                        <Select options={PERIOD_COUNT_OPTIONS} disabled />
                    </Form.Item>
                    <Form.Item
                        name="matchSegmentLength"
                        label="Period length (min)"
                        initialValue={timingDefaults.matchSegmentLength}
                        rules={[
                            {
                                required: true,
                                message: 'Period length is required!',
                            },
                        ]}
                        className="input-number-wrapper"
                    >
                        <InputNumber min={1} disabled />
                    </Form.Item>
                    <Form.Item
                        name="shotClockMaxSeconds"
                        label="Shot clock max (sec)"
                        initialValue={timingDefaults.shotClockMaxSeconds}
                        rules={[{ required: true, message: 'Shot clock max is required!' }]}
                        className="input-number-wrapper"
                    >
                        <InputNumber min={1} disabled />
                    </Form.Item>
                    <Form.Item
                        name="shotClockResetSeconds"
                        label="Shot clock reset (sec)"
                        initialValue={timingDefaults.shotClockResetSeconds}
                        rules={[{ required: true, message: 'Shot clock reset is required!' }]}
                        className="input-number-wrapper"
                    >
                        <InputNumber min={1} disabled />
                    </Form.Item>
                    <Form.Item
                        name="exclusionDurationSeconds"
                        label="Exclusion duration (sec)"
                        initialValue={timingDefaults.exclusionDurationSeconds}
                        rules={[{ required: true, message: 'Exclusion duration is required!' }]}
                        className="input-number-wrapper"
                    >
                        <InputNumber min={1} disabled />
                    </Form.Item>
                    <Form.Item
                        name="brutalityDurationMinutes"
                        label="Brutality duration (min)"
                        initialValue={timingDefaults.brutalityDurationMinutes}
                        rules={[{ required: true, message: 'Brutality duration is required!' }]}
                        className="input-number-wrapper"
                    >
                        <InputNumber min={0.1} step={0.5} disabled />
                    </Form.Item>
                </div>
            </Form>
        );
    }
}

export default MatchForm;
